import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { useConnectionStore } from "stores/ConnectionStore";

import { FOTS } from "utility/typescript";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

// Disable this check so we match the backend regex exactly.
// eslint-disable-next-line no-useless-escape
const MATTERMOST_WEBHOOK_REGEX =
  /^https:\/\/[a-zA-Z0-9-.]+\/hooks\/[a-zA-Z0-9-]+\/[a-zA-Z0-9-]+$/;

const MattermostWebhookConnectSetupPage = memo(
  function MattermostWebhookConnectSetupPage() {
    const [, connectionActions] = useConnectionStore();

    const [webhookUrl, setWebhookUrl] = useState("");
    const [inputError, setInputError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [uuid, setUuid] = useState("");

    const connect = () => {
      setLoading(true);
      connectionActions.mattermostWebhookConnect({
        webhookUrl,
        onSuccess,
        onError,
      });
    };

    const reset = () => {
      setWebhookUrl("");
      setInputError(false);
    };

    const onSuccess = (data: any) => {
      setLoading(false);
      toast.success("Successfully connected Mattermost Webhook", {
        position: "bottom-center",
      });
      connectionActions.loadConnections({ reload: true });
      setComplete(true);
      setUuid(data.uuid);
    };

    const onError = () => {
      setLoading(false);
      toast.error("Something went wrong connecting Mattermost Webhook", {
        position: "bottom-center",
      });
    };

    return (
      <>
        <Breadcrumbs>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Environment
          </Breadcrumb>
          <Breadcrumb
            underline="none"
            color="inherit"
            component={Link}
            to="/environment/destinations"
          >
            Destinations
          </Breadcrumb>
          <span />
        </Breadcrumbs>

        <ServiceConnectionInstructions
          label="Mattermost Webhook Connection"
          forceCollapse={complete ? new Date() : undefined}
        >
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight="bold">
              How ThreatKey Mattermost Webhooks work:
            </Typography>
            <Typography variant="body1">
              ThreatKey Mattermost Webhooks are a way for the ThreatKey platform
              to communicate with a Mattermost instance in near real-time. When
              an event happens in ThreatKey, a webhook is sent to the Mattermost
              instance with information about the event. The Mattermost instance
              can then take action based on the event.
            </Typography>
            <Typography variant="body1">
              You can use Mattermost webhooks to connect to Mattermost
              instances, to receive near real-time alerts of security events.
            </Typography>
          </Stack>
        </ServiceConnectionInstructions>

        {!complete ? (
          <ServiceConnectionStep disabled={loading}>
            <Typography variant="body1">
              Please paste the <strong>Mattermost Webhook URL</strong> that you
              would like to connect to.
            </Typography>
            <TextField
              fullWidth
              label="Mattermost Webhook URL"
              placeholder="https://{your-mattermost-site}/hooks/xxx-generatedkey-xxx
              "
              variant="outlined"
              sx={{ mt: 1 }}
              value={webhookUrl}
              onChange={(e: FOTS) => {
                const webhook = e?.target?.value.trim();
                const webhookValid = !(
                  webhook.match(MATTERMOST_WEBHOOK_REGEX) == null
                );

                setInputError(!!webhook && !webhookValid);
                setWebhookUrl(webhook);
              }}
              error={inputError}
              helperText={inputError ? "Invalid Mattermost Webhook URL" : ""}
              data-private="lipsum"
            />
            <Stack sx={{ mt: 2 }} spacing={2} direction="row">
              <LoadingButton
                variant="contained"
                onClick={connect}
                loading={loading}
                disabled={!webhookUrl || inputError}
              >
                Connect Mattermost Webhook
              </LoadingButton>
              <Button variant="text" onClick={reset}>
                Clear
              </Button>
            </Stack>
          </ServiceConnectionStep>
        ) : (
          <ServiceConnectionStep disabled={false}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1">Done!</Typography>
            </Box>
            <Stack sx={{ mt: 2 }} spacing={1} direction="column">
              <Typography variant="body1">
                <Link
                  to={`/environment/destinations/mattermost_webhook/${uuid}/notifications`}
                >
                  Click here
                </Link>{" "}
                to go to your Mattermost Webhook notification settings.
              </Typography>
            </Stack>
          </ServiceConnectionStep>
        )}
      </>
    );
  }
);

export default MattermostWebhookConnectSetupPage;
